/**
 * 通知系统统一类型定义和映射 (前端版本)
 * 
 * ⚠️ 重要：此文件与 apps/backend/src/types/notificationMapping.ts 需要保持同步
 * 修改时请同时更新后端对应文件
 */

/**
 * 提醒方式枚举
 * 用户在前端选择的提醒方式
 */
export enum ReminderMethod {
  SMS = '短信',
  WECHAT = '微信',
  EMAIL = '邮件',
  DESKTOP = '桌面通知',
}

/**
 * 提醒方式类型
 */
export type ReminderMethodType = ReminderMethod;

/**
 * 提醒方式数组类型
 */
export type ReminderMethods = ReminderMethodType[];

/**
 * 提醒方式配置
 * 包含显示标签、可用性等信息
 */
export interface ReminderMethodConfig {
  id: string;
  label: string;
  value: ReminderMethodType;
  available: boolean;
  description?: string;
}

/**
 * 提醒方式配置列表
 * 定义所有支持的提醒方式及其状态
 */
export const REMINDER_METHOD_CONFIGS: ReminderMethodConfig[] = [
  {
    id: 'desktop',
    label: '桌面通知',
    value: ReminderMethod.DESKTOP,
    available: true,
    description: '通过推送通知到设备',
  },
  {
    id: 'email',
    label: '邮件',
    value: ReminderMethod.EMAIL,
    available: true,
    description: '通过邮件发送通知',
  },
  {
    id: 'sms',
    label: '短信',
    value: ReminderMethod.SMS,
    available: false,
    description: '短信功能开发中，敬请期待',
  },
  {
    id: 'wechat',
    label: '微信',
    value: ReminderMethod.WECHAT,
    available: false,
    description: '微信功能开发中，敬请期待',
  },
];

/**
 * 基础提醒方式配置列表（包含翻译键）
 * 用于支持国际化的组件
 */
export const BASE_REMINDER_METHOD_CONFIGS = [
  {
    id: 'desktop',
    value: ReminderMethod.DESKTOP,
    available: true,
    labelKey: 'common:notifications.channels.pushNotification',
    descriptionKey: 'common:notifications.descriptions.pushNotification',
  },
  {
    id: 'email',
    value: ReminderMethod.EMAIL,
    available: true,
    labelKey: 'common:notifications.channels.email',
    descriptionKey: 'common:notifications.descriptions.email',
  },
  {
    id: 'sms',
    value: ReminderMethod.SMS,
    available: false,
    labelKey: 'common:notifications.channels.sms',
    descriptionKey: 'common:notifications.descriptions.sms',
  },
  {
    id: 'wechat',
    value: ReminderMethod.WECHAT,
    available: false,
    labelKey: 'common:notifications.channels.wechat',
    descriptionKey: 'common:notifications.descriptions.wechat',
  },
];

/**
 * 获取可用的提醒方式
 * @returns 当前可用的提醒方式配置列表
 */
export function getAvailableReminderMethods(): ReminderMethodConfig[] {
  return REMINDER_METHOD_CONFIGS.filter(config => config.available);
}

/**
 * 获取所有提醒方式
 * @returns 所有提醒方式配置列表
 */
export function getAllReminderMethods(): ReminderMethodConfig[] {
  return REMINDER_METHOD_CONFIGS;
}

/**
 * 根据 ID 获取提醒方式配置
 * @param id 提醒方式 ID
 * @returns 对应的配置，如果不存在则返回 undefined
 */
export function getReminderMethodConfig(id: string): ReminderMethodConfig | undefined {
  return REMINDER_METHOD_CONFIGS.find(config => config.id === id);
}

/**
 * 根据值获取提醒方式配置
 * @param value 提醒方式值
 * @returns 对应的配置，如果不存在则返回 undefined
 */
export function getReminderMethodConfigByValue(value: ReminderMethodType): ReminderMethodConfig | undefined {
  return REMINDER_METHOD_CONFIGS.find(config => config.value === value);
}

/**
 * 获取翻译后的提醒方式配置列表
 * @param t 翻译函数
 * @returns 翻译后的提醒方式配置列表
 */
export function getReminderMethodConfigs(t: (key: string, fallback?: string) => string): ReminderMethodConfig[] {
  return BASE_REMINDER_METHOD_CONFIGS.map(config => ({
    id: config.id,
    label: t(config.labelKey, config.labelKey.split('.').pop() || config.id),
    value: config.value,
    available: config.available,
    description: t(config.descriptionKey, config.descriptionKey.split('.').pop() || ''),
  }));
}

/**
 * 获取可用的翻译后提醒方式
 * @param t 翻译函数
 * @returns 当前可用的翻译后提醒方式配置列表
 */
export function getAvailableReminderMethodsI18n(t: (key: string, fallback?: string) => string): ReminderMethodConfig[] {
  return getReminderMethodConfigs(t).filter(config => config.available);
}

/**
 * 获取所有翻译后提醒方式
 * @param t 翻译函数
 * @returns 所有翻译后提醒方式配置列表
 */
export function getAllReminderMethodsI18n(t: (key: string, fallback?: string) => string): ReminderMethodConfig[] {
  return getReminderMethodConfigs(t);
}

/**
 * 验证提醒方式是否有效
 * @param method 提醒方式
 * @returns 是否为有效的提醒方式
 */
export function isValidReminderMethod(method: string): method is ReminderMethodType {
  return Object.values(ReminderMethod).includes(method as ReminderMethodType);
}

/**
 * 验证提醒方式列表是否有效
 * @param methods 提醒方式列表
 * @returns 是否所有提醒方式都有效
 */
export function areValidReminderMethods(methods: string[]): methods is ReminderMethodType[] {
  return methods.every(method => isValidReminderMethod(method));
}

/**
 * 格式化提醒方式列表为显示文本
 * @param methods 提醒方式列表
 * @returns 格式化后的显示文本
 */
export function formatReminderMethods(methods: ReminderMethodType[]): string {
  if (methods.length === 0) return '无';

  // 将内部的"桌面通知"转换为显示的"推送通知"
  const displayMethods = methods.map(method =>
    method === ReminderMethod.DESKTOP ? '推送通知' : method
  );

  if (displayMethods.length === 1) return displayMethods[0];
  if (displayMethods.length === 2) return displayMethods.join('、');
  return `${displayMethods.slice(0, -1).join('、')}等${displayMethods.length}种方式`;
}

/**
 * 格式化提醒方式列表为国际化显示文本
 * @param methods 提醒方式列表
 * @param t 翻译函数
 * @returns 格式化后的显示文本
 */
export function formatReminderMethodsI18n(methods: ReminderMethodType[], t: (key: string, fallback?: string) => string): string {
  if (methods.length === 0) return t('common:notifications.none', '无');

  // 获取翻译后的方式名称
  const displayMethods = methods.map(method => {
    const config = BASE_REMINDER_METHOD_CONFIGS.find(c => c.value === method);
    return config ? t(config.labelKey, method) : method;
  });

  const separator = t('common:notifications.separator', '、');
  if (displayMethods.length === 1) return displayMethods[0];
  if (displayMethods.length === 2) return displayMethods.join(separator);

  const methodsText = displayMethods.slice(0, -1).join(separator);
  return t('common:notifications.multipleFormat', '{{methods}}等{{count}}种方式')
    .replace('{{methods}}', methodsText)
    .replace('{{count}}', displayMethods.length.toString());
}

/**
 * 创建默认的提醒方式列表
 * @returns 默认的提醒方式列表
 */
export function createDefaultReminderMethods(): ReminderMethods {
  return [ReminderMethod.DESKTOP, ReminderMethod.EMAIL];
}

/**
 * 获取用户偏好的提醒方式列表
 * 如果用户未设置偏好，返回默认值
 * @param userPreferences 用户偏好设置
 * @returns 用户偏好的提醒方式列表
 */
export function getUserPreferredReminderMethods(userPreferences?: any): ReminderMethods {
  const notificationChannels = userPreferences?.notificationChannels;
  const defaultReminderMethods = notificationChannels?.defaultReminderMethods;

  if (Array.isArray(defaultReminderMethods) && defaultReminderMethods.length > 0) {
    // 验证用户设置的提醒方式是否有效
    const validMethods = Object.values(ReminderMethod);
    const validUserMethods = defaultReminderMethods.filter(method =>
      validMethods.includes(method as ReminderMethod)
    );

    if (validUserMethods.length > 0) {
      return validUserMethods as ReminderMethods;
    }
  }

  // 如果用户未设置或设置无效，返回默认值
  return createDefaultReminderMethods();
}

/**
 * 将中文枚举值映射到翻译文本
 * @param method 中文枚举值
 * @param t 翻译函数
 * @returns 翻译后的文本
 */
export function translateReminderMethod(method: ReminderMethodType, t: (key: string) => string): string {
  switch (method) {
    case ReminderMethod.DESKTOP:
      return t('common:notifications.channels.pushNotification');
    case ReminderMethod.EMAIL:
      return t('common:notifications.channels.email');
    case ReminderMethod.SMS:
      return t('common:notifications.channels.sms');
    case ReminderMethod.WECHAT:
      return t('common:notifications.channels.wechat');
    default:
      return method;
  }
}
